// services/accountSecurity.ts
import axios from 'axios';
import { Capacitor } from '@capacitor/core';

import axiosInstance from './axiosCreator';
import { getAuthClient, type AuthClientError } from './authClient';
import { SET_PASSWORD_PATH } from './authContracts';
import {
  DEMO_ACCOUNT_CODE,
  DEMO_ACCOUNT_MESSAGE,
  describeAuthError,
} from './auth';
import { getPasswordPolicyError } from '../utils/validEmail';
import {
  isSessionNotFresh,
  SESSION_NOT_FRESH_MESSAGE,
} from './sessionFreshness';
import {
  EMAIL_NOT_VERIFIED_MESSAGE,
  isEmailNotVerified,
} from './emailVerificationGate';

/**
 * Account → Security (ORA-987): two-factor authentication, connected sign-in
 * methods and setting a first password, on Better Auth.
 */

/** Better Auth's provider id for the email + password credential. */
export const CREDENTIAL_PROVIDER_ID = 'credential';

export type SignInProvider = 'credential' | 'google' | 'apple';

export interface LinkedAccount {
  /** Better Auth account row id (what `unlinkAccount` takes). */
  id: string;
  providerId: string;
}

export interface SecurityActionResult {
  success: boolean;
  error?: string;
  /** The session is over a day old: sign in again (`SESSION_NOT_FRESH`). */
  needsFreshSignIn?: boolean;
  /** The email must be verified first (`EMAIL_NOT_VERIFIED`, ORA-1089). */
  needsEmailVerification?: boolean;
}

/**
 * While an admin impersonates a student, the backend refuses every
 * security-changing route with 403 `IMPERSONATION_FORBIDDEN`.
 */
export const IMPERSONATION_FORBIDDEN_CODE = 'IMPERSONATION_FORBIDDEN';
export const IMPERSONATION_LOCKED_MESSAGE =
  'Security settings are locked while you impersonate a student.';

const describe = (
  error: AuthClientError | null | undefined,
  fallback: string
): string => {
  if (error?.code === IMPERSONATION_FORBIDDEN_CODE) {
    return IMPERSONATION_LOCKED_MESSAGE;
  }
  if (error?.code === 'INVALID_PASSWORD')
    return 'That password is not correct.';
  if (error?.code === 'INVALID_CODE') {
    return 'That code did not work. Check your authenticator app and try again.';
  }
  return describeAuthError(error, fallback);
};

const toResult = (
  error: AuthClientError | null | undefined,
  fallback: string
): SecurityActionResult => {
  if (!error) return { success: true };
  if (isSessionNotFresh(error)) {
    return {
      success: false,
      error: SESSION_NOT_FRESH_MESSAGE,
      needsFreshSignIn: true,
    };
  }
  if (isEmailNotVerified(error)) {
    return {
      success: false,
      error: EMAIL_NOT_VERIFIED_MESSAGE,
      needsEmailVerification: true,
    };
  }
  return { success: false, error: describe(error, fallback) };
};

/** The account's sign-in methods (credential, google, apple). */
export const listLinkedAccounts = async (): Promise<LinkedAccount[]> => {
  const { data, error } = await getAuthClient().listAccounts();
  if (error || !Array.isArray(data)) {
    throw new Error(describe(error, 'Could not load your sign-in methods.'));
  }
  return data.map(({ id, providerId }) => ({ id, providerId }));
};

export const hasPasswordAccount = (accounts: LinkedAccount[]): boolean =>
  accounts.some((account) => account.providerId === CREDENTIAL_PROVIDER_ID);

export const unlinkLinkedAccount = async (
  accountId: string
): Promise<SecurityActionResult> => {
  try {
    const { error } = await getAuthClient().unlinkAccount({ accountId });
    if (error?.code === 'FAILED_TO_UNLINK_LAST_ACCOUNT') {
      return {
        success: false,
        error: 'You need at least one way to sign in.',
      };
    }
    return toResult(error, 'Could not disconnect that account.');
  } catch {
    return { success: false, error: 'Could not disconnect that account.' };
  }
};

/**
 * Adds an email + password sign-in to a Google/Apple-only account
 * (`POST /auth/set-password`, cookie-authenticated).
 */
export const setAccountPassword = async (
  newPassword: string
): Promise<SecurityActionResult> => {
  const policyError = getPasswordPolicyError(newPassword);
  if (policyError) return { success: false, error: policyError };

  try {
    await axiosInstance.post(SET_PASSWORD_PATH, { newPassword });
    return { success: true };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      const data = error.response?.data as
        { code?: string; error?: string } | undefined;

      if (status === 409) {
        return {
          success: false,
          error: 'Your account already has a password.',
        };
      }
      if (data?.code === IMPERSONATION_FORBIDDEN_CODE) {
        return { success: false, error: IMPERSONATION_LOCKED_MESSAGE };
      }
      if (data?.code === DEMO_ACCOUNT_CODE) {
        return { success: false, error: DEMO_ACCOUNT_MESSAGE };
      }
      // A first password needs a sign-in under ten minutes old.
      if (isSessionNotFresh(data)) {
        return {
          success: false,
          error: SESSION_NOT_FRESH_MESSAGE,
          needsFreshSignIn: true,
        };
      }
      if (data?.error) return { success: false, error: data.error };
    }
    return { success: false, error: 'Could not set your password.' };
  }
};

/** Whether the signed-in account has two-factor authentication on. */
export const fetchTwoFactorEnabled = async (): Promise<boolean> => {
  const { data, error } = await getAuthClient().getSession({
    query: { disableCookieCache: true },
  });

  if (error) throw new Error('Could not load your two-factor status.');
  return (
    (data?.user as { twoFactorEnabled?: boolean | null } | undefined)
      ?.twoFactorEnabled === true
  );
};

export interface TwoFactorEnrolment {
  totpURI: string;
  /** The base32 secret inside `totpURI`, for manual entry. */
  secret: string;
  backupCodes: string[];
}

/** The base32 secret of an `otpauth://` URI, or '' when it has none. */
export const readTotpSecret = (totpURI: string): string => {
  try {
    return new URL(totpURI).searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
};

/**
 * Starts enrolment: Better Auth creates the secret and backup codes; 2FA
 * turns on only once `confirmTwoFactorEnrolment` verifies a first code.
 * `password` is omitted for Google/Apple-only accounts.
 */
export const startTwoFactorEnrolment = async (
  password?: string
): Promise<
  { success: true; enrolment: TwoFactorEnrolment } | SecurityActionResult
> => {
  try {
    const { data, error } = await getAuthClient().twoFactor.enable(
      password ? { password } : {}
    );

    const totpURI = data && 'totpURI' in data ? data.totpURI : undefined;
    if (error || !totpURI) {
      return toResult(
        error ?? { message: 'Could not start two-factor setup.' },
        'Could not start two-factor setup.'
      );
    }

    return {
      success: true,
      enrolment: {
        totpURI,
        secret: readTotpSecret(totpURI),
        backupCodes: data && 'backupCodes' in data ? data.backupCodes : [],
      },
    };
  } catch {
    return { success: false, error: 'Could not start two-factor setup.' };
  }
};

export const confirmTwoFactorEnrolment = async (
  code: string
): Promise<SecurityActionResult> => {
  try {
    const { error } = await getAuthClient().twoFactor.verifyTotp({ code });
    return toResult(error, 'That code did not work.');
  } catch {
    return { success: false, error: 'That code did not work.' };
  }
};

export const disableTwoFactor = async (
  password?: string
): Promise<SecurityActionResult> => {
  try {
    const { error } = await getAuthClient().twoFactor.disable(
      password ? { password } : {}
    );

    return toResult(error, 'Could not turn off two-factor authentication.');
  } catch {
    return {
      success: false,
      error: 'Could not turn off two-factor authentication.',
    };
  }
};

export const regenerateBackupCodes = async (
  password?: string
): Promise<{ success: true; backupCodes: string[] } | SecurityActionResult> => {
  try {
    const { data, error } = await getAuthClient().twoFactor.generateBackupCodes(
      password ? { password } : {}
    );

    if (error || !data?.backupCodes) {
      return toResult(
        error ?? { message: 'Could not create new backup codes.' },
        'Could not create new backup codes.'
      );
    }
    return { success: true, backupCodes: data.backupCodes };
  } catch {
    return { success: false, error: 'Could not create new backup codes.' };
  }
};

/** The web app a verification link opens: the native shells' own origin
 * (`capacitor://localhost`) means nothing to a browser. */
const WEB_APP_ORIGIN = 'https://app.oraai.com';

/** Where Better Auth sends the browser once the email is verified. */
export const verificationCallbackUrl = (path: string): string =>
  `${Capacitor.isNativePlatform() ? WEB_APP_ORIGIN : window.location.origin}${path}`;

/** The Security tab's own return after a verification. */
export const SECURITY_VERIFIED_CALLBACK_PATH =
  '/account?sub=security&verified=1';

/**
 * A verification link opened without the account's own session: the backend
 * redirects with `error=SIGN_IN_REQUIRED` and verifies nothing (ORA-1089).
 */
export const VERIFICATION_SIGN_IN_REQUIRED_ERROR = 'SIGN_IN_REQUIRED';
export const VERIFICATION_SIGN_IN_REQUIRED_MESSAGE =
  "This link only works where you're signed in to Ora. Don't know the password? Reset it — that verifies your email too.";

/** Router state that opens the auth page on Forgot Password (the address
 * never rides in a URL). */
export interface OpenForgotPasswordState {
  forgotPassword: true;
}

export const OPEN_FORGOT_PASSWORD_STATE: OpenForgotPasswordState = {
  forgotPassword: true,
};

/** A verification link that came back refused to the Security tab. */
export const SECURITY_VERIFICATION_FAILED_MESSAGE =
  "That verification link didn't work. Use Verify Email below to get a new one.";

/** Any other refusal of a verification link (expired, invalid). */
export const VERIFICATION_LINK_FAILED_MESSAGE =
  "That verification link didn't work. Request a new one from your account's Security settings.";

const VERIFICATION_SEND_FAILED_MESSAGE =
  'We could not send the verification email. Please try again.';

/** Better Auth refuses to send for another address than the signed-in
 * account's (`EMAIL_MISMATCH`): the auth page with a live cookie for another
 * account. */
export const VERIFICATION_EMAIL_MISMATCH_MESSAGE =
  "You're signed in as another account. Sign out first, then try again.";

/**
 * Sends the primary email's verification link (Better Auth
 * `POST /send-verification-email`). The backend answers 200 whatever the
 * address, so success says only that the request went through; a 429 is a
 * rate limit.
 */
export const sendVerificationEmail = async (
  email: string,
  callbackPath: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await getAuthClient().sendVerificationEmail({
      email: email.trim(),
      callbackURL: verificationCallbackUrl(callbackPath),
    });

    if (!error) return { success: true };
    return {
      success: false,
      error:
        error.code === 'EMAIL_MISMATCH'
          ? VERIFICATION_EMAIL_MISMATCH_MESSAGE
          : describeAuthError(error, VERIFICATION_SEND_FAILED_MESSAGE),
    };
  } catch {
    return { success: false, error: VERIFICATION_SEND_FAILED_MESSAGE };
  }
};
